import { toApiError } from "./classifyFailure";
import type {
  ApplyResult,
  Change,
  Command,
  Interval,
  ProjectTree,
  SavedFilter,
  Series,
  SeriesId,
  Sort,
  SyncReport,
  TaskId,
  TaskView,
  When,
} from "./generated";
import { SyncStatusTracker } from "./syncStatusTracker";
import type { ApiEvent, SyncStatus, TaskApi, Unsubscribe } from "./taskApi";

/**
 * Rust's JSON has `null` where the generated types (and the wasm boundary) have `undefined`,
 * so replies are normalized once here and the UI sees one shape.
 */
export function nullsToUndefined<T>(value: T): T {
  return withoutNulls(value) as T;
}

function withoutNulls(value: unknown): unknown {
  if (value === null) return undefined;
  if (Array.isArray(value)) return (value as unknown[]).map(withoutNulls);
  if (typeof value === "object") {
    const entries = Object.entries(value).map(([key, member]) => [key, withoutNulls(member)]);
    return Object.fromEntries(entries);
  }
  return value;
}

export const CHANGE_EVENT = "td://change";

/** The slice of `@tauri-apps/api` the adapter needs, so tests can stand in for the shell. */
export interface TauriBridge {
  invoke<T>(command: string, args?: Record<string, unknown>): Promise<T>;
  listen(event: string, handler: (payload: unknown) => void): Promise<() => void>;
}

/** `TaskApi` over Tauri commands: the replica lives in the Rust shell, on disk. */
export class TauriTaskApi implements TaskApi {
  private readonly listeners = new Set<(event: ApiEvent) => void>();
  private readonly syncTracker = new SyncStatusTracker();

  constructor(private readonly bridge: TauriBridge) {
    this.syncTracker.onStatusChange((status) => {
      this.emit({ kind: "sync-status", status });
    });
  }

  /** Starts forwarding the shell's change events; resolves once they will be heard. */
  async start(): Promise<void> {
    await this.bridge.listen(CHANGE_EVENT, (payload) => {
      const change = nullsToUndefined(payload as Change);
      this.syncTracker.observe(change);
      this.emit({ kind: "change", change });
    });
  }

  query(filter: string, sort: Sort): Promise<TaskView[]> {
    return this.call("query", { filter, sort });
  }

  get(id: TaskId): Promise<TaskView | undefined> {
    return this.call<TaskView | null>("get", { id }).then((view) => view ?? undefined);
  }

  projects(): Promise<ProjectTree> {
    return this.call("projects");
  }

  savedFilters(): Promise<SavedFilter[]> {
    return this.call("saved_filters");
  }

  series(id: SeriesId): Promise<Series | undefined> {
    return this.call<Series | null>("series", { id }).then((series) => series ?? undefined);
  }

  seriesInstances(id: SeriesId): Promise<TaskView[]> {
    return this.call("series_instances", { id });
  }

  parseDate(text: string): Promise<When> {
    return this.call("parse_date", { text });
  }

  parseCalendarDay(text: string): Promise<string> {
    return this.call("parse_calendar_day", { text });
  }

  parseInterval(text: string): Promise<Interval> {
    return this.call("parse_interval", { text });
  }

  apply(command: Command): Promise<ApplyResult> {
    return this.call("apply", { command });
  }

  undo(): Promise<ApplyResult> {
    return this.call("undo");
  }

  redo(): Promise<ApplyResult> {
    return this.call("redo");
  }

  sync(): Promise<SyncReport> {
    return this.syncTracker.track(() => this.call<SyncReport>("sync"));
  }

  syncStatus(): SyncStatus {
    return this.syncTracker.status();
  }

  subscribe(listener: (event: ApiEvent) => void): Unsubscribe {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: ApiEvent): void {
    this.listeners.forEach((listener) => listener(event));
  }

  private async call<T>(command: string, args?: Record<string, unknown>): Promise<T> {
    try {
      return nullsToUndefined(await this.bridge.invoke<T>(command, args));
    } catch (failure) {
      throw toApiError(failure);
    }
  }
}
